import React, { ReactNode, useEffect, useState } from "react";
import { Panel, PanelGroup } from "react-resizable-panels";
import { intl } from "LocaleWrapper";
import moment, { Moment } from "moment";
import { useLazyQuery, useMutation } from "@apollo/client";
import { useStoreState } from "ezStore";

import { Button, Card, Filter, PageHeader } from "@cofano/cck";
import { FilterSettingsProviderProps } from "@cofano/cck/Filter/FilterSettingsProvider";
import { Alert, Box, CircularProgress, Grid2, Slider, styled, Tab, Tabs, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import ViewAgendaIcon from "@mui/icons-material/ViewAgenda";

import Helmet from "components/helmet/Helmet";
import Planboard from "components/planboard2/Planboard";
import { settingsProvider } from "components/utils/TableUtils";
import ResizeHandle from "components/resizeContainer/ResizeHandle";

import ShipSelect, { ShipSelectOption } from "view/ships/components/selects/ShipSelect";
import VoyageAddDialog from "view/voyages/components/dialogs/VoyageAddDialog";
import FleetSelect, { FleetSelectOption } from "view/fleets/components/selects/FleetSelect";

import { shortid } from "imports/Utils";

import { ShipLink } from "utils/LinkUtils";

import ContainerPlanningTable, { ContainerFromQuery } from "../components/table/ContainerPlanningTable";
import ContainerPlanningCallTask from "../components/tasks/ContainerPlanningCallTask";
import ContainerPlanningVoyageTask from "../components/tasks/ContainerPlanningVoyageTask";

import { ChildProps, ChildPropsFromArray } from "types/commons";
import { CallType, ContainerShipPlanningQuery } from "__generated__/graphql";
import { PlanCargoActionsDocument } from "__generated__/view/voyages/graphql/voyage.mutations.generated";
import { gql } from "__generated__/gql";

import "./styling.scss";

type ShipPlanning = ChildProps<ContainerShipPlanningQuery, "shipPlanning">;
type ShipPlanningItem = ChildPropsFromArray<ContainerShipPlanningQuery, "shipPlanning">;

type Ship = ChildProps<ShipPlanningItem, "ship">;
type Voyage = ChildPropsFromArray<ShipPlanningItem, "voyages">;
export type Call = ChildPropsFromArray<Voyage, "calls">;

type Group = {
	id: number;
	entity: Ship
	droppable: boolean;
	title: ReactNode;
}

type Item = {
	id: number;
	entity: Call;
	group: number;
	start: Moment;
	end: Moment;
	className?: string;
	canMove?: boolean;
	canResize?: boolean;
	title: ReactNode;
}

// eslint-disable-next-line react-refresh/only-export-components
export enum PlannedOrUnplanned {
	PLANNED = "planned",
	UNPLANNED = "unplanned"
}

// eslint-disable-next-line react-refresh/only-export-components
export enum PlannedOrExpected {
	PLANNED = "planned",
	EXPECTED = "expected"
}

const FilterWrapper = styled(Box)(({ theme }) => ({
	"& .cck-filter": {
		boxShadow: "0 1px 1px 0 rgba(0,0,0,0.14)",
		position: "relative",

		"& .MuiCollapse-root": {
			backgroundColor: theme.palette.background.paper,
			boxShadow: "0 1px 1px 0 rgba(0, 0 ,0, 0.14)",
			position: "absolute",
			width: "100%",
			zIndex: 101,
		},
	},
}));

const ContainerPlanning = () => {

	const session = useStoreState(state => state.session);

	const { filter: filterSettingsProvider } = settingsProvider();

	const settings = filterSettingsProvider.settings("containerPlanning_filter") as FilterSettingsProviderProps | undefined;

	const [ filter, setFilter ] = useState<{
		fleets: FleetSelectOption[] | undefined,
		ships: ShipSelectOption[] | undefined,
		window: [number, number],
	}>({
		fleets: settings?.fields?.find(item => item.id === "fleets")?.value ?? session.user?.properties.fleets,
		ships: settings?.fields?.find(item => item.id === "ships")?.value ?? undefined,
		window: settings?.fields?.find(item => item.id === "window")?.value ?? [ -14, 14 ],
	});

	const [ state, setState ] = useState<{ shipPlanning: ShipPlanning, groups: Group[]; items: Item[] }>({
		shipPlanning: [],
		groups: [],
		items: [],
	});

	const localStorageStacked = localStorage.getItem("containerPlanningStacked");

	const [ layoutColumnSizes, setLayoutColumnSizes ] = useState(localStorage.getItem("containerPlanningLayoutColumnSizes") ? JSON.parse(localStorage.getItem("containerPlanningLayoutColumnSizes") as string) as [number, number] : [ 50, 50 ]);
	const [ stacked, setStacked ] = useState(localStorageStacked ? JSON.parse(localStorageStacked) : true);
	const [ selectedTab, selectTab ] = useState<PlannedOrUnplanned>(PlannedOrUnplanned.PLANNED);
	const [ addVoyageDialogOpen, openAddVoyageDialog ] = useState(false);
	const [ selectedCall, selectCall ] = useState<Call | undefined>(undefined);
	const [ plannedOrExpected, setPlannedOrExpected ] = useState<PlannedOrExpected>(PlannedOrExpected.PLANNED);
	const [ loadingOrDischarging, setLoadingOrDischarging ] = useState(CallType.Loading);
	const [ updateUnplannedTableKey, setUpdateUnplannedTableKey ] = useState(0);

	const [ dragging, setDragging ] = useState<{ enabled: boolean, containers: ContainerFromQuery[] }>({
		enabled: false,
		containers: [],
	});

	const [ getShipsPlanning, { loading, refetch } ] = useLazyQuery(query, {
		onCompleted: response => {
			setState(oldState => ({
				...oldState,
				shipPlanning: response.shipPlanning ?? [],
			}));
		},
	});

	const [ planCargoActions, { loading: planCargoActionsLoading } ] = useMutation(PlanCargoActionsDocument);

	const onPlan = (call: Call, voyage: Voyage, containers: ContainerFromQuery[]) => {

		planCargoActions({
			variables: {
				cargoActionIds: containers.map(c => {
					const action = c.actions.find(a => a.location?.id === call.location.id);
					return action?.id;
				}) as number[],
				callId: call.id,
				voyageId: voyage.id,

			},
		}).then(() => {
			refetch();
			setUpdateUnplannedTableKey(updateUnplannedTableKey + 1);
		});
	};

	const createGroups = (planning: ShipPlanning | undefined) => {

		const groups: Group[] = [];

		planning?.forEach((item) => {
			if (item?.ship) {
				groups.push({
					id: item.ship.id,
					entity: item.ship,
					droppable: true,
					title: <ShipLink ship={item.ship} />,
				});
			}
		});

		return groups;
	};

	const createItems = (planning: ShipPlanning | undefined) => {

		const items: Item[] = [];

		planning?.forEach((item) => {
			item?.voyages?.forEach((voyage) => {
				voyage?.calls.forEach((call, index) => {

					const start = plannedOrExpected === PlannedOrExpected.PLANNED ? moment(call.pta) : moment(call.eta);
					const end = plannedOrExpected === PlannedOrExpected.PLANNED ? moment(call.ptd) : moment(call.etd);
					const isSelected = call.id === selectedCall?.id;
					const droppable = isSelected && dragging.enabled;

					items.push({
						id: shortid.generate(),
						entity: call,
						group: item.ship?.id ?? 0,
						start: start,
						end: end,
						title: droppable ? (
							<div
								className="call-task"
								style={{ backgroundColor: "green" }}
								onDrop={(event) => {
									const containers = JSON.parse(event.dataTransfer.getData("container-planning-containers"));
									onPlan(call, voyage, containers);
								}}
								onDragOver={(event) => {
									event.preventDefault();
								}}
								onDragEnter={(event) => {
									event.preventDefault();
									const target = event.target as HTMLElement;
									target.style.background = "#86c288";
									target.style.borderRadius = "inherit";
								}}
								onDragLeave={(event) => {
									event.preventDefault();
									(event.target as HTMLElement).style.background = "blue";
								}}>
								{call.location.code || call.location.description}
							</div>
						) : (
							<ContainerPlanningCallTask
								call={call}
								voyage={voyage}
								onClick={(c) => selectCall(c.id !== selectedCall?.id ? c : undefined)}
								fadeOut={!!(selectedCall && !isSelected)}
								isSelected={isSelected}
								isPlanned={plannedOrExpected === PlannedOrExpected.PLANNED}
								session={session}
								refetch={refetch} />
						),
					});

					if (voyage.calls[index + 1]) {

						const start = plannedOrExpected === PlannedOrExpected.PLANNED ? moment(call.ptd) : moment(call.etd);
						const end = plannedOrExpected === PlannedOrExpected.PLANNED ? moment(voyage.calls[index + 1].pta) : moment(voyage.calls[index + 1].eta);

						items.push({
							id: shortid.generate(),
							entity: call,
							group: item.ship?.id ?? 0,
							start: start,
							end: end,
							title: (
								<ContainerPlanningVoyageTask
									call={call}
									fadeOut={!!selectedCall} />
							),
						});
					}
				});
			});
		});

		return items;
	};

	const getShipsFromPlanning = () => {
		const options = [] as ShipSelectOption[];

		state.shipPlanning.forEach(it => {
			if ( it?.ship) {
				options.push(it.ship);
			}
		});

		return options;
	};

	const onLayout = (sizes: number[]) => {
		setLayoutColumnSizes(sizes);
		localStorage.setItem("locationPlanningLayoutColumnSizes", JSON.stringify(sizes));
	};

	useEffect(() => {
		localStorage.setItem("containerPlanningStacked", JSON.stringify(stacked));
	}, [ stacked ]);

	useEffect(() => {
		setState(oldState => ({
			...oldState,
			groups: createGroups(oldState.shipPlanning),
			items: createItems(oldState.shipPlanning),
		}));
	}, [ state.shipPlanning, dragging.enabled, selectedCall?.id, plannedOrExpected ]);

	useEffect(() => {
		getShipsPlanning({
			variables: {
				filters: {
					fleetIds: filter.fleets?.map((it) => it.id),
					shipIds: filter.ships ? filter.ships.map(ship => ship.id) : undefined,
					from: moment().startOf("day").add("days", filter.window[0]).unix() * 1000,
					to: moment().endOf("day").add("days", filter.window[1]).unix() * 1000,
					availability: true,
					capacity: true,
					cargos: true,
					allShips: true,
				},
				detailedMode: false,
			},
		});
	}, [ filter ]);

	const shipsFromPlanning = getShipsFromPlanning();

	return (
		<div className={`container-planning ${stacked ? "stacked" : "side-by-side"}`}>
			<PageHeader>
				<PageHeader.Title>
					{intl.formatMessage({ id: "PLANNING" })}
					<Helmet title={intl.formatMessage({ id: "PLANNING" })} />
				</PageHeader.Title>

				<PageHeader.Actions>
					<Button
						onClick={() => setStacked(true)}
						icon={<ViewAgendaIcon />}
						disabled={stacked}
						variant="text" />

					<Button
						onClick={() => setStacked(false)}
						icon={<ViewAgendaIcon style={{ transform: "rotate(-90deg)" }} />}
						disabled={!stacked}
						variant="text" />

					<Button onClick={() => openAddVoyageDialog(true)} disabled={shipsFromPlanning.length === 0}>
						{intl.formatMessage({ id: "VOYAGE_ADD" })}
					</Button>
				</PageHeader.Actions>
			</PageHeader>

			<div className="resize-container">
				<PanelGroup direction="horizontal" onLayout={onLayout} className="panel-group">
					<Panel minSize={10} defaultSize={layoutColumnSizes[0]} className="panel">
						<div className="panel-board">
							<div style={{ marginBottom: 16 }}>
								<Card disablePadding>
									<FilterWrapper>
										<Filter
											id='containerPlanning_filter'
											datePickerPortalId='boardDatePicker'
											settingsProvider={filterSettingsProvider}
											defaultOpen={false}
											fields={[ {
												id: "fleets",
												title: intl.formatMessage({ id: "FLEETS_SINGLE" }),
												render: (value, onChange) => <FleetSelect value={value} onChange={onChange} multiple />,
												defaultValue: filter.fleets,
											}, {
												id: "ships",
												title: intl.formatMessage({ id: "SHIPS" }),
												render: (value, onChange) => <ShipSelect value={value} onChange={onChange} multiple />,
												defaultValue: filter.ships,
											}, {
												id: "window",
												title: `${intl.formatMessage({ id: "PERIOD" })} (${intl.formatMessage({ id: "DAYS_FORWARD_AND_BACKWARD" })})`,
												render: (value, onChange) => (
													<div className="period-slider">
														<Slider
															aria-label="Always visible"
															valueLabelDisplay="on"
															value={value || [ -14, 14 ]}
															min={-90}
															max={90}
															size='small'
															marks={[ {
																value: -90,
																label: "-90",
															}, {
																value: 90,
																label: "90",
															} ]}
															onChange={(event, v) => onChange(v)} />
													</div>
												),
												defaultValue: filter.window,
											} ]}
											onSearch={newValues => {
												setFilter({
													fleets: newValues.find(item => item.property === "fleets")?.value,
													ships: newValues.find(item => item.property === "ships")?.value,
													window: newValues.find(item => item.property === "window")?.value,
												});
											}} />
									</FilterWrapper>
								</Card>
							</div>

							<Card disablePadding>
								{/*@ts-expect-error*/}
								<Planboard
									id='containerPlanning'
									planboardKey={`${stacked}-${layoutColumnSizes[1]}`}
									loading={loading}
									groups={state.groups}
									items={state.items}
									stackItems={true}
									onRefresh={() => refetch()}
									optionalTools={(
										<div>
											<ToggleButtonGroup
												color="primary"
												value={plannedOrExpected}
												exclusive
												onChange={(event, value) => setPlannedOrExpected(value)}
												size="small"
												style={{ marginRight: 8 }}>
												<ToggleButton value={PlannedOrExpected.PLANNED}>{intl.formatMessage({ id: "PLANNED" })}</ToggleButton>
												<ToggleButton value={PlannedOrExpected.EXPECTED}>{intl.formatMessage({ id: "EXPECTED" })}</ToggleButton>
											</ToggleButtonGroup>
										</div>
									)}
								/>
							</Card>
						</div>
					</Panel>

					<ResizeHandle />

					<Panel minSize={10} defaultSize={layoutColumnSizes[1]} className="panel">
						<div className="panel-table">
							<Card disablePadding>
								<Box sx={{ borderBottom: 1, borderColor: "divider" }}>
									<Grid2 container>
										<Grid2 size={6}>
											<Tabs value={selectedTab} onChange={(event, newValue) => selectTab(newValue)}>
												<Tab label={intl.formatMessage({ id: "PLANNED_CARGOS" })} value={PlannedOrUnplanned.PLANNED} />
												<Tab label={intl.formatMessage({ id: "UNPLANNED_CARGOS" })} value={PlannedOrUnplanned.UNPLANNED} />
											</Tabs>
										</Grid2>

										<Grid2 size={6}>
											{planCargoActionsLoading ? (
												<div>
													<Alert severity="info" icon={<CircularProgress size={22} color="info" />}>
														<Typography variant="caption">
															{intl.formatMessage({ id: "PLAN_IN_PROGRESS" })}
														</Typography>
													</Alert>
												</div>
											) : (
												<React.Fragment>
													{selectedCall ? (
														<Alert severity="success">
															<Typography variant="caption">
																{intl.formatMessage({ id: "CALL_SELECTED_PLAN_CONTAINERS" })}
															</Typography>

														</Alert>
													) : (
														<Alert severity="info">
															<Typography variant="caption">
																{intl.formatMessage({ id: "SELECT_CALL_TO_PLAN_CONTAINERS" })}
															</Typography>
														</Alert>
													)}
												</React.Fragment>
											)}

										</Grid2>
									</Grid2>

								</Box>

								<Box sx={{ borderBottom: 1, borderColor: "divider", marginBottom: 1 }}>
									<Tabs value={loadingOrDischarging} onChange={(event, newValue) => setLoadingOrDischarging(newValue)}>
										<Tab label={intl.formatMessage({ id: "LOADING" })} value={CallType.Loading} />
										<Tab label={intl.formatMessage({ id: "DISCHARGING" })} value={CallType.Discharging} />
									</Tabs>
								</Box>

								<div>
									{selectedTab === PlannedOrUnplanned.PLANNED && (
										<ContainerPlanningTable
											onStartDragging={(containers) => setDragging({ enabled: true, containers: containers })}
											onFinishDragging={() => setDragging({ enabled: false, containers: [] })}
											conditions={{
												isUnplanned: false,
												loadingOrDischarging: loadingOrDischarging,
												call: selectedCall,
											}}
											refetch={refetch} />
									)}

									{selectedTab === PlannedOrUnplanned.UNPLANNED && (
										<ContainerPlanningTable
											onStartDragging={(containers) => setDragging({ enabled: true, containers: containers })}
											onFinishDragging={() => setDragging({ enabled: false, containers: [] })}
											conditions={{
												isUnplanned: true,
												loadingOrDischarging: loadingOrDischarging,
												call: selectedCall,
											}}
											refetch={refetch}
											updateUnplannedTableKey={updateUnplannedTableKey} />
									)}
								</div>
							</Card>
						</div>
					</Panel>
				</PanelGroup>
			</div>

			{addVoyageDialogOpen && (
				<VoyageAddDialog
					open={addVoyageDialogOpen}
					onClose={() => openAddVoyageDialog(false)}
					onSave={() => refetch()}
					customShips={shipsFromPlanning}
				/>
			)}
		</div>
	);
};

const query = gql(`#graphql
	query containerShipPlanning($filters: ShipPlanningFilters, $detailedMode: Boolean!) {
		shipPlanning(filters: $filters, detailedMode: $detailedMode) {
			ship {
				id
				name
				operator {
					id
					name
				}
				owner {
					id
					name
				}
			}
			voyages {
				id
				voyageNumber
				start
				end
				ship {
					id
					name
				}
				calls {
					id
					location {
						id
						description
						code
						displayCode
					}
					actualStatusCode
					lastStatusChange {
						status {
							description
							color
						}
					}
					remainingTeuCapacity
					remainingWeightCapacity
					loadedTeuOnVoyageAfterCall
					loadedWeightOnVoyageAfterCall
					expectedLoadingAmount
					expectedDischargingAmount
					expectedLoadingTeu
					expectedDischargingTeu
					nrLoadingCargos
					nrDischargingCargos
					numberOfLoadingContainers {
						...numberOfContainersFragment
					}
					numberOfDischargingContainers {
						...numberOfContainersFragment
					}
					numberOfLoadingReeferContainers {
						...numberOfContainersFragment
					}
					numberOfDischargingReeferContainers {
						...numberOfContainersFragment
					}
					expectedCargos {
						id
						amount
						actionType
						dischargingLocation {
							id
							description
						}
						containerType {
							isoTypeCode
							displayCode
							lengthFt
							teu
							reefer
						}
						full
					}
					voyage {
						id
						calls {
							id
							location {
								id
							}
						}
					}
					...CallTimes
				}
			}
		}
	}
`);

export default ContainerPlanning;
